import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { ArrowLeft, LogOut, Calendar, Clock, User, Loader2, Trash2 } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { api, type Conferencia } from "@/lib/api";
import { parseMySQLDateLocal, formatarDataLocal, formatarHoraLocal } from "@/lib/dateUtils";
import { formatDisplayText } from "@/lib/formatDisplayText";
import { PAGE_TITLE_STANDALONE_CLASS } from "@/lib/modalStyles";
import { toast } from "@/lib/appToast";
import { PageContentLoader } from "@/components/PageContentLoader";

export default function HistoricoConferencias() {
  const { user, logout } = useAuth();
  const [, setLocation] = useLocation();
  const [conferencias, setConferencias] = useState<Conferencia[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const isAdmin = user?.role === "admin";

  const carregarHistorico = useCallback(async () => {
    try {
      setLoading(true);
      const lista = await api.listarConferencias();
      setConferencias(Array.isArray(lista) ? lista : []);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar histórico");
      setConferencias([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void carregarHistorico();
  }, [carregarHistorico]);

  const handleExcluirConferencia = async (id: number) => {
    if (!confirm("Deseja apagar esta conferência?")) return;
    try {
      setDeletingId(id);
      await api.excluirConferencia(id);
      setConferencias((prev) => prev.filter((c) => c.id !== id));
      toast.success("Conferência excluída");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao excluir conferência");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-accent text-accent-foreground sticky top-0 z-50 shadow-lg">
        <div className="container max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/dashboard")}
              className="text-accent-foreground hover:bg-accent/80"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className={`${PAGE_TITLE_STANDALONE_CLASS} text-accent-foreground`}>Histórico de Conferências</h1>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="text-accent-foreground hover:bg-accent/80"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
        </div>
      </div>

      <div className="container max-w-4xl mx-auto px-4 py-6 pb-20">
        {loading ? (
          <PageContentLoader />
        ) : (
        <Card>
          <CardHeader>
            <CardDescription>
              Registro completo de todas as conferências de estoque realizadas
            </CardDescription>
          </CardHeader>
          <CardContent>
            {conferencias.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Nenhuma conferência registrada ainda.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {conferencias.map((conf) => (
                  <div
                    key={conf.id}
                    className="p-4 border rounded-lg bg-card hover:border-accent/50 transition-all flex flex-col sm:flex-row sm:items-start justify-between gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-2">
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Data</p>
                          <p className="font-semibold flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-accent" />
                            {formatarDataLocal(parseMySQLDateLocal(conf.data_conferencia))}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Horário</p>
                          <p className="font-semibold flex items-center gap-2">
                            <Clock className="w-4 h-4 text-accent" />
                            {formatarHoraLocal(parseMySQLDateLocal(conf.data_conferencia))}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Conferido por</p>
                          <p className="font-semibold flex items-center gap-2">
                            <User className="w-4 h-4 text-accent" />
                            {formatDisplayText(conf.usuario_nome)}
                          </p>
                        </div>
                      </div>
                      {conf.observacoes && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Observações</p>
                          <p className="text-foreground">{formatDisplayText(conf.observacoes)}</p>
                        </div>
                      )}
                    </div>
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void handleExcluirConferencia(conf.id)}
                        disabled={deletingId === conf.id}
                        className="text-muted-foreground hover:text-destructive flex-shrink-0 p-2"
                        aria-label="Excluir conferência"
                      >
                        {deletingId === conf.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </div>
    </div>
  );
}
